import type { OnboardingStepKey } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const ONBOARDING_ROUTES = { PROGRESS: '/configuracao/progresso' } as const;

const ENDPOINT = '/api/v1/onboarding';

export interface OnboardingStep {
  key: OnboardingStepKey;
  title: string;
  hint: string;
  href: string;
  done: boolean;
  count: number;
}

export interface OnboardingProgress {
  completed: number;
  total: number;
  dismissedAt: string | null;
  steps: OnboardingStep[];
}

export function useOnboarding() {
  return useQuery({
    queryKey: ['onboarding'],
    queryFn: () => apiClient.get<OnboardingProgress>(`${ENDPOINT}/progress`),
  });
}

export function useDismissOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.post<OnboardingProgress>(`${ENDPOINT}/dismiss`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });
}

export interface WorkDay {
  weekday: number;
  startTime: string;
  endTime: string;
  breakStart?: string | null;
  breakEnd?: string | null;
}

export function useProfessionalSchedule(professionalId: string) {
  return useQuery({
    queryKey: ['professional-schedule', professionalId],
    queryFn: () =>
      apiClient.get<{ data: WorkDay[] }>(`/api/v1/professionals/${professionalId}/schedule`),
    enabled: Boolean(professionalId),
  });
}

export function useSaveProfessionalSchedule(professionalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (days: WorkDay[]) =>
      apiClient.put<{ data: WorkDay[] }>(`/api/v1/professionals/${professionalId}/schedule`, {
        days,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professional-schedule', professionalId] });
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}
